'use client';
import Image from 'next/image';
import Link from 'next/link';
import blogsData from '@/textContent/blogs/blogsData.json';
import { motion } from 'framer-motion';

/* ---------------- Animation Variants ---------------- */

const fadeUp = {
    hidden: { opacity: 0, y: 40 },
    visible: {
        opacity: 1,
        y: 0,
        transition: { duration: 0.6, ease: 'easeOut' },
    },
};

const staggerContainer = {
    hidden: {},
    visible: {
        transition: { staggerChildren: 0.12 },
    },
};

const scaleIn = {
    hidden: { opacity: 0, scale: 0.9 },
    visible: {
        opacity: 1,
        scale: 1,
        transition: { duration: 0.4, ease: 'easeOut' },
    },
};

/* ---------------- CASE STUDY CARD ---------------- */

const CaseStudyCard = ({
    meta_image,
    name,
    title,
    blogger_data,
    read_time,
}) => {
    return (
        <div className="h-full bg-white border border-gray-100 rounded-2xl overflow-hidden shadow-sm hover:shadow-xl transition-all duration-300 ease-in-out transform hover:-translate-y-1.5 cursor-pointer group flex flex-col">
            <Link
                rel="canonical"
                href={`/learn/case-studies/${name}`}
                className="flex flex-col h-full"
            >
                {/* IMAGE */}
                <div className="relative w-full h-56 overflow-hidden">
                    <Image
                        fill
                        src={`${process.env.NEXT_PUBLIC_API_ENDPOINT}${meta_image}`}
                        alt="Case Study Image"
                        className="object-cover transition-transform duration-500 ease-in-out group-hover:scale-105"
                        sizes="(max-width: 768px) 100vw, (max-width: 1024px) 50vw, 33vw"
                    />
                    <span className="absolute top-4 left-4 bg-white/90 backdrop-blur text-blue-600 text-xs font-bold px-3 py-1 rounded-full">
                        Case Study
                    </span>
                </div>

                {/* CONTENT */}
                <div className="p-6 flex flex-col flex-1">
                    {/* TITLE */}
                    <p className="text-lg font-bold mb-3 text-gray-800 group-hover:text-blue-600 transition-colors duration-300 line-clamp-2 min-h-[3.5rem]">
                        {title}
                    </p>

                    {/* FOOTER */}
                    <div className="mt-auto pt-4 border-t border-gray-100 flex items-center justify-between gap-2">
                        <div className="flex items-center">
                            <div className="relative w-9 h-9 shrink-0">
                                <Image
                                    fill
                                    src={`${process.env.NEXT_PUBLIC_API_ENDPOINT}${blogger_data.avatar}`}
                                    alt="Profile Image"
                                    className="rounded-full ring-2 ring-gray-200 group-hover:ring-blue-400 transition-all duration-300 object-cover"
                                />
                            </div>
                            <p className="ml-3 text-sm font-semibold text-gray-700">
                                {blogger_data.full_name}
                            </p>
                        </div>

                        <div className="flex items-center gap-1.5 bg-blue-50 text-blue-600 text-xs font-semibold px-3 py-1.5 rounded-full whitespace-nowrap">
                            <i className="ri-time-line"></i>
                            {read_time} min read
                        </div>
                    </div>
                </div>
            </Link>
        </div>
    );
};

/* ---------------- CASE STUDY HOME SECTION ---------------- */

export default function CaseStudyHome() {
    return (
        <motion.section
            variants={fadeUp}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            className="pt-6 md:pt-8 pb-12 md:pb-16 lg:pb-20 bg-white"
        >
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                <div className="text-center mb-12">
                    <p className="text-blue-500 font-bold text-sm mb-3">
                        Success Stories
                    </p>
                    <h2 className="text-3xl md:text-4xl font-bold text-gray-800">
                        Case Studies
                    </h2>
                    <p className="mt-3 text-lg text-gray-600">
                        Real results achieved by our clients
                    </p>
                </div>
                <motion.div
                    variants={staggerContainer}
                    className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 md:gap-10"
                >
                    {blogsData
                        .filter(
                            (blog) =>
                                blog.blog_category === 'website-case-studies',
                        )
                        .slice(0, 3)
                        .map((blog, index) => (
                            <motion.div key={index} variants={scaleIn}>
                                <CaseStudyCard {...blog} />
                            </motion.div>
                        ))}
                </motion.div>

                {/* View All Button */}
                <div className="text-center mt-12">
                    <Link rel="canonical" href="/learn/case-studies">
                        <button className="hello_button py-3 px-8 bg-blue-500 rounded-md shadow-xl hover:shadow-none transition duration-300">
                            <span className="font-bold text-white text-sm md:text-base">
                                View All Case Studies
                            </span>
                        </button>
                    </Link>
                </div>
            </div>
        </motion.section>
    );
}
